import React from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../../components/auth/authTheme";
import { MobileFormFooter } from "../profileViewHelpers";

const SectionCard = ({ title, icon, isMobile, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "14px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? 1.5 : 2,
      boxShadow: isMobile ? "0 1px 6px rgba(26, 43, 74, 0.04)" : "none",
    }}
  >
    {title && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              bgcolor: BRAND.orangeLight,
              color: BRAND.orangeHover,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "0.9375rem" }}>
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
);

const AccountDetailEditForm = ({ userDetails, onCancel, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2;

  const initialValues = {
    account_number: userDetails?.employee_salary?.account_number || "",
    bank_name: userDetails?.employee_salary?.bank_name || "",
    bank_branch: userDetails?.employee_salary?.bank_branch || "",
    ifsc_code: userDetails?.employee_salary?.ifsc_code || "",
    pan_number: userDetails?.employee_salary?.pan_number || "",
    pf_number: userDetails?.employee_salary?.pf_number || "",
    esi_number: userDetails?.employee_salary?.esi_number || "",
  };

  const validationSchema = Yup.object().shape({
    account_number: Yup.string().required("Required"),
    ifsc_code: Yup.string().required("Required"),
  });

  const handleSubmit = (values) => {
    onSave({
      id: userDetails?.id,
      employee_salary: {
        ...userDetails?.employee_salary,
        ...values,
      },
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, touched, errors, isSubmitting }) => (
        <Form>
          <Grid container spacing={gridSpacing} sx={{ ml: 0, width: "100%" }}>
            <Grid item xs={12}>
              <SectionCard
                title="Bank Details"
                icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />}
                isMobile={isMobile}
              >
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Account Number"
                      fullWidth
                      name="account_number"
                      value={values.account_number}
                      onChange={handleChange}
                      error={touched.account_number && Boolean(errors.account_number)}
                      helperText={touched.account_number && errors.account_number}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bank Name"
                      fullWidth
                      name="bank_name"
                      value={values.bank_name}
                      onChange={handleChange}
                      error={touched.bank_name && Boolean(errors.bank_name)}
                      helperText={touched.bank_name && errors.bank_name}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Branch"
                      fullWidth
                      name="bank_branch"
                      value={values.bank_branch}
                      onChange={handleChange}
                      error={touched.bank_branch && Boolean(errors.bank_branch)}
                      helperText={touched.bank_branch && errors.bank_branch}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="IFSC Code"
                      fullWidth
                      name="ifsc_code"
                      value={values.ifsc_code}
                      onChange={handleChange}
                      error={touched.ifsc_code && Boolean(errors.ifsc_code)}
                      helperText={touched.ifsc_code && errors.ifsc_code}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard
                title="Statutory Details"
                icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
                isMobile={isMobile}
              >
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="PAN Number"
                      fullWidth
                      name="pan_number"
                      value={values.pan_number}
                      onChange={handleChange}
                      error={touched.pan_number && Boolean(errors.pan_number)}
                      helperText={touched.pan_number && errors.pan_number}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="PF Number"
                      fullWidth
                      name="pf_number"
                      value={values.pf_number}
                      onChange={handleChange}
                      error={touched.pf_number && Boolean(errors.pf_number)}
                      helperText={touched.pf_number && errors.pf_number}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="ESI Number"
                      fullWidth
                      name="esi_number"
                      value={values.esi_number}
                      onChange={handleChange}
                      error={touched.esi_number && Boolean(errors.esi_number)}
                      helperText={touched.esi_number && errors.esi_number}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              {isMobile ? (
                <MobileFormFooter onCancel={onCancel} isSubmitting={isSubmitting} />
              ) : (
                <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
                  <Button variant="outlined" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" sx={primaryButtonSx}>
                    Save
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AccountDetailEditForm;
